import {TimezoneService} from 'src/app/services/timezone.service';
import {ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {AbstractControl, FormArray, FormControl, FormGroup} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {select, Store} from '@ngrx/store';
import {combineLatest, filter, Observable, startWith, Subscription, take} from 'rxjs';
import {Project} from 'src/app/core/model/project';
import {RewardCoin} from 'src/app/core/model/reward';
import {SkillModel} from 'src/app/core/model/skill';
import {ProjectTask, TaskTemplate} from 'src/app/core/model/task';
import {User} from 'src/app/core/model/user';
import {DeleteImageAction} from 'src/app/core/store/active-user/active-user.action';
import {
  AddProjectTaskAction,
  CreateTaskTemplateAction,
  DeleteProjectTaskAction,
  DeleteTaskTemplateAction,
  GetTaskTemplatesAction,
  UpdateProjectTaskAction,
  UpdateTaskTemplateAction,
} from 'src/app/core/store/projects/projects.action';
import {selectSkillsMap} from 'src/app/core/store/skills/skills.selector';
import {selectAllTeams, selectTeamsMap} from 'src/app/core/store/teams/teams.selector';
import {GetAllTrackersAction} from 'src/app/core/store/trackers/trackers.action';
import {selectAllUsers, selectUsersMap} from 'src/app/core/store/users/users.selector';
import {Team} from 'src/app/teams/shared/model/team';
import {MessageService} from '../../../services/message.service';
import {OphIconModule} from '../../design/oph-icon/oph-icon.module';
import {OphInputsModule} from '../../design/oph-inputs/oph-inputs.module';
import {TasksService} from '../../services/tasks.service';
import {NewTrackerInDialogComponent} from '../../trackers/new-tracker-in-dialog/new-tracker-in-dialog.component';
import {TaskIconPickerComponent} from '../icon-picker/task-icon-picker.component';
import {
  EDIT_PROJECT_TASK_DIALOG_SETTINGS,
  NEW_PROJECT_TASK_DIALOG_SETTINGS,
  ProjectTaskDialogPanels,
} from './project-task-dialog-constants';
import {ProjectTaskDialogTabs} from './project-task-dialog-constants';
import {ProjectsTaskDateTimePickerComponent} from './date-time-picker/projects-task-date-time-picker.component';
import {ProjectsTaskDefaultTabsComponent} from './default-tabs/projects-task-default-tabs.component';
import {ProjectsTaskDetailsComponent} from './details/projects-task-details.component';
import {ProjectsTaskNewItemWrapperComponent} from './new-item-wrapper/projects-task-new-item-wrapper.component';
import {ProjectsTaskScheduleComponent} from './schedule/projects-task-schedule.component';
import {ProjectsTaskSubtasksComponent} from './subtask/projects-task-subtasks.component';
import {ProjectsTaskTriggersComponent} from './triggers/projects-task-triggers.component';
import {ProjectsTaskDefaultFooterComponent} from './projects-task-default-footer/projects-task-default-footer.component';
import * as moment from 'moment';
import {TaskDialogRequirementsComponent} from '../requirements/task-dialog-requirements.component';
import {ProjectsUsersTeamsSelectionComponent} from 'src/app/projects/users-teams-selection/projects-users-teams-selection.component';
import {selectProjectsMap} from 'src/app/core/store/projects/projects.selector';
import {TaskIconPickerButtonComponent} from '../icon-picker-button/task-icon-picker-button.component';

interface DialogData {
  newTask?: boolean;
  taskTemplate?: TaskTemplate;
  isTemplate?: boolean;
  task: ProjectTask;
  coinTypes: RewardCoin[];
  project: Project;
  stepId?: string;
  taskIndex?: number;
  templateSearchValue?: string;
}

@Component({
  selector: 'projects-task-dialog',
  standalone: true,
  imports: [
    ProjectsTaskNewItemWrapperComponent,
    OphInputsModule,
    ProjectsTaskDetailsComponent,
    OphIconModule,
    ProjectsTaskTriggersComponent,
    NewTrackerInDialogComponent,
    ProjectsTaskScheduleComponent,
    ProjectsTaskDateTimePickerComponent,
    ProjectsTaskSubtasksComponent,
    ProjectsTaskDefaultTabsComponent,
    ProjectsTaskDefaultFooterComponent,
    MatProgressSpinnerModule,
    TaskDialogRequirementsComponent,
    ProjectsUsersTeamsSelectionComponent,
    TaskIconPickerButtonComponent,
    TaskIconPickerComponent,
  ],
  templateUrl: './projects-task-dialog.component.html',
  styleUrl: './projects-task-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'oph-scroll-bar',
  },
})
export class ProjectsTaskDialogComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<ProjectsTaskDialogComponent>);
  readonly data = inject<DialogData>(MAT_DIALOG_DATA);
  private tasksService = inject(TasksService);
  private store$ = inject(Store);
  private messageService = inject(MessageService);
  private timezoneService = inject(TimezoneService);

  teams$: Observable<Team[]> = this.store$.pipe(select(selectAllTeams));
  users$: Observable<User[]> = this.store$.pipe(select(selectAllUsers));
  teamsMap$: Observable<Record<string, Team>> = this.store$.pipe(select(selectTeamsMap));
  usersMap$: Observable<Record<string, User>> = this.store$.pipe(select(selectUsersMap));
  skillsMap$: Observable<Record<string, SkillModel>> = this.store$.pipe(select(selectSkillsMap));
  projectsMap$: Observable<Record<string, Project>> = this.store$.pipe(select(selectProjectsMap));

  activePanel = signal<ProjectTaskDialogPanels>(
    this.data.newTask && !this.data.taskTemplate
      ? ProjectTaskDialogPanels.NewBasicInfo
      : ProjectTaskDialogPanels.Default
  );
  project = signal<Project>(null);
  isTriggerPanelValid = signal<boolean>(false);
  isSchedulePanelValid = signal<boolean>(false);
  dateTimeControlName = signal<string | null>(null);
  dateTimeControl = signal<FormControl | null>(null);
  selectedTab = signal<ProjectTaskDialogTabs>(ProjectTaskDialogTabs.Details);
  subtaskCount = signal<number>(0);
  triggerCount = signal<number>(0);
  addPeopleData = signal<{
    projectId: string;
    users: User[];
    usersMap: Record<string, User>;
    teams: Team[];
    teamsMap: Record<string, Team>;
  }>(null);

  // Additional properties for save functionality
  subscriptions = new Subscription();
  validationErrors = signal<Record<string, boolean>>({});
  loadingSave = signal<boolean>(false);
  loadingDelete = signal<boolean>(false);
  minDate = signal<moment.Moment | null>(null);
  isOneTriggerComplete: boolean;
  imagesToDelete: string[] = [];
  stepId: string;
  skipToSubtasks = false;
  ProjectTaskDialogPanels = ProjectTaskDialogPanels;
  ProjectTaskDialogTabs = ProjectTaskDialogTabs;
  storedPanel: ProjectTaskDialogPanels;
  storedTrackerIndex: number;

  form: FormGroup;

  ngOnInit() {
    if (this.data.newTask && !this.data.taskTemplate) {
      this.dialogRef.updateSize(NEW_PROJECT_TASK_DIALOG_SETTINGS.height);
    } else {
      this.dialogRef.updateSize(EDIT_PROJECT_TASK_DIALOG_SETTINGS.height);
    }
    this.teamsMap$
      .pipe(
        filter(t => !!t),
        take(1)
      )
      .subscribe(teamsMap => {
        const combinedUserObject = {};
        this.data.project.users.forEach(userId => {
          combinedUserObject[userId] = true;
        });
        this.data.project.teams.forEach(teamId => {
          teamsMap[teamId]?.users?.forEach(user => {
            combinedUserObject[user._id] = true;
          });
        });
        this.form = this.data.isTemplate
          ? this.tasksService.getTaskTemplateForm(this.data?.taskTemplate || null)
          : this.tasksService.getTaskForm(
              this.data?.task || null,
              false,
              this.data.coinTypes?.[0]?._id,
              Object.keys(combinedUserObject)
            );
      });

    // Initialize tracker dispatch and trigger count subscription
    this.store$.dispatch(new GetAllTrackersAction({params: {projectId: this.data.project._id, pageSize: 1000}}));
    this.subscriptions.add(this.subscribeToProjectsMap());
    if (!this.data.isTemplate) {
      this.subscriptions.add(this.setTriggerCount());
    }
    this.subscriptions.add(this.setSubtaskCount());
    this.stepId = this.getStepId(this.data.project);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  subscribeToProjectsMap(): Subscription {
    return this.projectsMap$.pipe(filter(p => !!p)).subscribe(projectsMap => {
      this.project.set(projectsMap[this.data.project._id]);
    });
  }

  getStepId(project: Project): string {
    const activeStep = project.steps.find(step => step.status === 'active');
    if (activeStep) return activeStep._id;

    const inactiveStep = project.steps.find(step => step.status === 'inactive');
    if (inactiveStep) return inactiveStep._id;

    const completeStep = project.steps.find(step => step.status === 'complete');
    return completeStep?._id || '';
  }

  setTriggerCount(): Subscription {
    return this.triggerFormArray?.valueChanges.pipe(startWith(this.triggerFormArray.value)).subscribe(triggers => {
      if (!triggers) {
        return;
      }
      let triggerCount = 0;
      const triggerCompleteArray = [];
      triggers.forEach(trigger => {
        if (this.tasksService.checkIfTriggerIsComplete(trigger)) {
          triggerCount++;
          triggerCompleteArray.push(true);
        } else {
          triggerCompleteArray.push(false);
        }
      });
      this.triggerCount.set(triggerCount);
      this.isOneTriggerComplete = triggerCompleteArray.some(complete => complete);
    });
  }

  setSubtaskCount(): Subscription {
    return this.subtaskFormArray?.valueChanges.pipe(startWith(this.subtaskFormArray.value)).subscribe(subtasks => {
      if (!subtasks) {
        return;
      }
      this.subtaskCount.set(subtasks.length);
    });
  }

  subscribeToScheduleChanges(): Subscription {
    return this.form.valueChanges.subscribe(() => this.checkForValidationErrors());
  }

  onSave(duplicate?: boolean) {
    if (this.data.isTemplate) {
      this.onSaveTemplate();
      return;
    }
    this.checkForValidationErrors();
    // Start watching for event time changes to re-validate
    this.subscriptions.add(this.subscribeToScheduleChanges());

    if (!this.validate()) {
      return;
    }

    this.loadingSave.set(true);
    if (!this.stepId && !this.data.stepId) {
      this.messageService.add('Step id not found. This is most likely a bug. Please contact support.');
      return;
    }

    const formattedTask = this.tasksService.formatProjectTaskFromForm(this.form);
    const task = {...this.data.task, ...formattedTask};
    if (this.data.newTask || duplicate) {
      this.addProjectTask(task);
      return;
    }

    this.updateProjectTask(task);
  }

  onSaveTemplate(manuallyCreateTemplate?: boolean) {
    if (!this.form.valid) {
      return;
    }
    this.loadingSave.set(true);
    if (!!this.data.taskTemplate && !manuallyCreateTemplate) {
      this.updateTaskTemplate();
    } else {
      this.createTaskTemplate();
    }
  }

  validate(): boolean {
    const isInvalidWeeklyRepeat =
      this.scheduleTypeControl.value === 'repeats' &&
      this.repeatTypeControl.value === 'weeks' &&
      this.repeatDaysControl.value?.length === 0;
    const hasIncompleteCycleEndsTrigger =
      this.scheduleTypeControl.value === 'repeats' &&
      this.scheduleControls.get('cycleEndsType').value === 'trigger' &&
      !this.tasksService.checkIfTriggerIsComplete(this.scheduleControls.get('cycleEndsTrigger').value);

    const hasIncompleteTriggers =
      this.scheduleTypeControl.value === 'trigger' && !this.isOneTriggerComplete && this.triggerFormArray.length > 0;

    if (isInvalidWeeklyRepeat || hasIncompleteTriggers || hasIncompleteCycleEndsTrigger) {
      this.activePanel.set(ProjectTaskDialogPanels.Default);
      this.selectedTab.set(ProjectTaskDialogTabs.Schedule);
      return false;
    }
    this.validationErrors.set({});
    return this.form.valid;
  }

  createTaskTemplate() {
    const task = this.tasksService.formatTaskTemplateFromForm(this.form);
    this.store$.dispatch(
      new CreateTaskTemplateAction({
        task,
        onSuccess: (task: ProjectTask) => this.onSaveTaskTemplateSuccess(),
        onFailure: err => this.loadingSave.set(false),
      })
    );
  }

  updateTaskTemplate() {
    const formattedTask = this.tasksService.formatTaskTemplateFromForm(this.form);
    const task = {...this.data.taskTemplate, ...formattedTask};

    this.store$.dispatch(
      new UpdateTaskTemplateAction({
        task,
        onSuccess: (task: ProjectTask) => this.onSaveTaskTemplateSuccess(),
        onFailure: err => this.onSaveTaskTemplateFailure(),
      })
    );
  }

  onSaveTaskTemplateSuccess() {
    this.store$.dispatch(new GetTaskTemplatesAction({search: this.data.templateSearchValue}));
    this.dialogRef.close(true);
    this.form.reset();
    this.onDeleteSubtaskImages();
  }

  onSaveTaskTemplateFailure() {
    this.loadingSave.set(false);
    this.messageService.add('There was a problem saving the task.');
  }

  addProjectTask(task: ProjectTask) {
    this.store$.dispatch(
      new AddProjectTaskAction({
        projectId: this.data.project._id,
        stepId: this.data.stepId || this.stepId,
        task,
        taskIndex: this.data.taskIndex,
        onSuccess: () => this.onUpdateProjectSuccess(),
        onFailure: err => this.onUpdateProjectFailure(err),
      })
    );
  }

  updateProjectTask(task: ProjectTask) {
    this.store$.dispatch(
      new UpdateProjectTaskAction({
        projectId: this.data.project._id,
        stepId: this.data.stepId || this.stepId,
        task,
        onSuccess: () => this.onUpdateProjectSuccess(),
        onFailure: err => this.onUpdateProjectFailure(err),
      })
    );
  }

  onUpdateProjectSuccess() {
    if (this.data.newTask) {
      this.onChangePanel(ProjectTaskDialogPanels.NewSuccess);
    } else {
      this.dialogRef.close();
    }
    this.form.reset();
    this.loadingSave.set(false);
    this.onDeleteSubtaskImages();
  }

  onUpdateProjectFailure(err: Error) {
    this.messageService.add(err?.message);
    this.loadingSave.set(false);
  }

  checkForValidationErrors() {
    this.validationErrors.set({
      repeatDays: this.scheduleTypeControl.value === 'repeats' && this.repeatDaysControl.value?.length === 0,
      scheduleRepeatsTracker:
        this.scheduleTypeControl.value === 'repeats' &&
        this.scheduleControls.get('cycleEndsType').value === 'trigger' &&
        !this.tasksService.checkIfTriggerIsComplete(this.scheduleControls.get('cycleEndsTrigger').value),
      isOneTriggerComplete: !this.isOneTriggerComplete,
    });
  }

  onImagesToDeleteChange(imagesToDelete: string[]) {
    this.imagesToDelete = imagesToDelete;
  }

  onDeleteSubtaskImages() {
    if (!this.imagesToDelete?.length) {
      return;
    }
    const idString = this.imagesToDelete.join(',');
    const ids = encodeURIComponent(idString);
    this.store$.dispatch(
      new DeleteImageAction({
        ids,
        onFailure: err => this.messageService.add(err.error),
      })
    );
  }

  findAllTaskImagesToDelete() {
    let images: string[] = [];
    // Find all the images to delete along with the task
    (this.form.value.subtasks || []).forEach((subtask, index) => {
      images = [...images, ...this.tasksService.getImgArrayFromHTMLString(subtask.body)];
    });
    this.imagesToDelete = [...this.imagesToDelete, ...images];
    this.onDeleteSubtaskImages();
  }

  onChangePanel(panel: ProjectTaskDialogPanels, dateTimeControlName?: string) {
    this.storedPanel = this.activePanel();
    if (dateTimeControlName) {
      this.dateTimeControlName.set(dateTimeControlName);
      if (dateTimeControlName === 'scheduleOnDate') {
        this.dateTimeControl.set(this.form.get('schedule')?.get(dateTimeControlName) as FormControl);
      }
      if (dateTimeControlName === 'cycleEndsTrigger') {
        this.dateTimeControl.set(this.form.get('schedule')?.get('cycleEndsTrigger')?.get('value') as FormControl);
      }
    }
    if (dateTimeControlName === 'expirationDate') {
      const scheduleOnDate = this.form?.get('schedule')?.get('scheduleOnDate')?.value;
      const minDate = moment(scheduleOnDate).tz(this.timezoneService.getTimeZone());
      this.minDate.set(minDate);
    } else {
      this.minDate.set(null);
    }
    this.activePanel.set(panel);
  }

  onSkipToSubtasks() {
    this.skipToSubtasks = true;
    this.onChangePanel(ProjectTaskDialogPanels.NewSubtasks);
  }

  onIconChange(value: string) {
    if (value) {
      this.form.get('icon').setValue(value);
    }
    if (this.data.newTask) {
      this.onChangePanel(ProjectTaskDialogPanels.NewBasicInfo);
    } else {
      this.onChangePanel(ProjectTaskDialogPanels.Default);
    }
  }

  onCancel() {
    if (this.data.newTask) {
      this.storedPanel = this.activePanel();
      this.onChangePanel(ProjectTaskDialogPanels.NewCancel);
    } else {
      this.onCloseDialog();
    }
  }

  onCloseDialog() {
    this.dialogRef.close(false);
  }

  onNewTracker(trackerIndex: number) {
    this.storedTrackerIndex = trackerIndex;
    this.storedPanel = this.activePanel();
    this.onChangePanel(ProjectTaskDialogPanels.NewTracker);
  }

  onCloseNewTrackerInDialog(trackerId: string) {
    if (this.storedTrackerIndex) {
      (this.form.get('triggers') as FormArray).at(this.storedTrackerIndex).patchValue({trackerId});
    }
    this.onChangePanel(this.storedPanel);
  }

  onCreateAnotherTask() {
    this.form.reset();
    (this.form.get('subtasks') as FormArray).clear();
    (this.form.get('triggers') as FormArray).clear();
    this.onChangePanel(ProjectTaskDialogPanels.NewBasicInfo);
  }

  onTabChange(tab: ProjectTaskDialogTabs) {
    this.selectedTab.set(tab);
  }

  onDelete() {
    this.storedPanel = this.activePanel();
    if (this.data.isTemplate) {
      this.onChangePanel(ProjectTaskDialogPanels.DeleteTemplateConfirmation);
    } else {
      this.onChangePanel(ProjectTaskDialogPanels.DeleteTaskConfirmation);
    }
  }

  onDuplicate() {
    this.onSave(true);
  }

  onCreateTemplate() {
    this.onSaveTemplate(true);
  }

  deleteProjectTask() {
    if (!this.data.task?.taskInstanceId) {
      this.messageService.add('Cannot delete task: task instance ID not found.');
      return;
    }

    this.loadingDelete.set(true);
    this.store$.dispatch(
      new DeleteProjectTaskAction({
        projectId: this.data.project._id,
        taskInstanceId: this.data.task.taskInstanceId,
        onSuccess: () => this.onDeleteSuccess(),
        onFailure: err => this.onDeleteFailure(err),
      })
    );
  }

  deleteTaskTemplate() {
    if (!this.data.taskTemplate?._id) {
      this.messageService.add('Cannot delete template: template ID not found.');
      return;
    }

    this.loadingDelete.set(true);
    this.store$.dispatch(
      new DeleteTaskTemplateAction({
        id: `${this.data.taskTemplate._id}`,
        onSuccess: () => this.onDeleteTemplateSuccess(),
        onFailure: err => this.onDeleteFailure(err),
      })
    );
  }

  onDeleteSuccess() {
    this.dialogRef.close();
    this.loadingDelete.set(false);
    this.form.reset();
  }

  onDeleteFailure(err: any) {
    this.loadingDelete.set(false);
    this.messageService.add(err?.error || 'There was a problem deleting the task');
  }

  onDeleteTemplateSuccess() {
    this.findAllTaskImagesToDelete();
    this.store$.dispatch(new GetTaskTemplatesAction({search: this.data.templateSearchValue}));
    this.dialogRef.close();
    this.loadingDelete.set(false);
    this.form.reset();
  }

  onConfirmRemoveTask() {
    this.deleteProjectTask();
    this.findAllTaskImagesToDelete();
  }

  onConfirmDeleteTemplate() {
    this.deleteTaskTemplate();
  }

  onConfirmationCancel() {
    this.onChangePanel(this.storedPanel || ProjectTaskDialogPanels.Default);
  }

  onDateTimePickerChange() {
    this.form.get('schedule')?.get('expirationDate')?.setValue(this.tasksService.getExpirationDate(this.form.value));
    this.onChangePanel(this.storedPanel);
    this.dateTimeControl.set(null);
    this.dateTimeControlName.set(null);
  }

  onAddPeople() {
    combineLatest([this.users$, this.usersMap$, this.teams$, this.teamsMap$])
      .pipe(take(1))
      .subscribe(([users, usersMap, teams, teamsMap]) => {
        this.addPeopleData.set({projectId: this.data.project._id, users, usersMap, teams, teamsMap});
        this.onChangePanel(ProjectTaskDialogPanels.PeopleTeamsSelection);
      });
  }

  onModifyUserTeamList(event: {type: 'user' | 'team'; action: 'add' | 'remove'; id: string}) {
    if (event.type === 'user') {
      this.handleModifyUser(event.id, event.action);
    } else {
      this.handleModifyTeam(event.id, event.action);
    }
  }

  handleModifyTeam(teamId: string, action: 'add' | 'remove') {
    this.teamsMap$.pipe(take(1)).subscribe(teamsMap => {
      const userIds = teamsMap[teamId]?.users?.map(user => user._id) || [];
      if (action === 'add') {
        const currentUsers = this.form.get('users').value;
        const uniqueUsers = [...new Set([...currentUsers, ...userIds])];
        this.form.get('users').setValue(uniqueUsers);
      } else {
        // When removing a team, only remove users who are NOT directly assigned to the project
        const currentUsers = this.form.get('users').value;
        const projectUsers = this.data.project.users || [];
        const filteredUsers = currentUsers.filter(id => !userIds.includes(id) || projectUsers.includes(id));
        this.form.get('users').setValue(filteredUsers);
      }
    });
  }

  handleModifyUser(userId: string, action: 'add' | 'remove') {
    if (action === 'add') {
      const currentUsers = this.form.get('users').value;
      if (!currentUsers.includes(userId)) {
        this.form.get('users').setValue([...currentUsers, userId]);
      }
    } else {
      // Check if user is part of a team assigned to the project before removing
      this.teamsMap$.pipe(take(1)).subscribe(teamsMap => {
        const projectTeams = this.data.project.teams || [];

        // Check if user is member of any team assigned to the project
        const isUserInAssignedTeam = projectTeams.some(teamId => {
          const team = teamsMap[teamId];
          return team?.users?.some(user => user._id === userId);
        });

        // Only remove user if they are not part of an assigned team
        if (!isUserInAssignedTeam) {
          this.form.get('users').setValue(this.form.get('users').value.filter(id => id !== userId));
        }
        // Note: User stays in the list if they are part of an assigned team
      });
    }
  }

  // Form control getters
  get eventControl(): AbstractControl {
    return this.form.get('event');
  }

  get scheduleControls(): AbstractControl {
    return this.form.get('schedule');
  }

  get eventTimesControls(): AbstractControl {
    return this.scheduleControls.get('eventTimes');
  }

  get scheduleTypeControl(): AbstractControl {
    return this.scheduleControls.get('scheduleType');
  }

  get repeatTypeControl(): AbstractControl {
    return this.scheduleControls.get('repeatType');
  }

  get repeatDaysControl(): AbstractControl {
    return this.scheduleControls.get('repeatDays');
  }

  get eventStartControl(): AbstractControl {
    return this.eventTimesControls.get('eventStart');
  }

  get eventEndControl(): AbstractControl {
    return this.eventTimesControls.get('eventEnd');
  }

  get triggerFormArray(): FormArray {
    return this.form.get('triggers') as FormArray;
  }

  get subtaskFormArray(): FormArray {
    return this.form.get('subtasks') as FormArray;
  }
}
